import { useNavigate, useParams } from "@solidjs/router";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  Show,
} from "solid-js";
import {
  deviceTopic,
  type AttributeDefinition,
  type DeviceDetail,
  type DeviceInfo,
  type LatestValue,
} from "@energyplatform/client";
import { api, live } from "../services.ts";
import { locale, t, tOr } from "../i18n.ts";
import {
  capabilityId,
  formatValue,
  groupByChannel,
  hasValue,
} from "../model.ts";
import { counterIncrements } from "../series.ts";
import { formatAgo, formatDateTime, isStale } from "../format.ts";
import { range, setScope } from "../context.ts";
import { Icon } from "../icons.tsx";
import { Chart, type ChartSeries } from "../components/Chart.tsx";
import { CategoryBadge, categoryRank } from "../components/Media.tsx";
import { ErrorBox } from "../components/ErrorBox.tsx";
import { PlaceDeviceDialog, placesOf } from "../components/PlaceDevice.tsx";
import {
  ConfirmDialog,
  operator,
  PageHeader,
  RenameDialog,
  Skeleton,
  Tabs,
  useAction,
} from "../components/ui.tsx";

const TABS = ["values", "history", "sources", "capabilities"] as const;
type Tab = (typeof TABS)[number];

/** Devices grouped by category on the left, the selected device's card on the right. */
export function DevicesPage() {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [devices, { refetch }] = createResource(() => api.devices.list());
  const [query, setQuery] = createSignal("");
  const selected = () =>
    devices()?.find((d) => d.id === params.id) ?? devices()?.[0];

  const groups = createMemo(() => {
    const q = query().trim().toLocaleLowerCase();
    const map = new Map<string, DeviceInfo[]>();
    for (const d of devices() ?? []) {
      if (
        q === "" ||
        d.name.toLocaleLowerCase().includes(q) ||
        d.templateId.toLocaleLowerCase().includes(q)
      ) {
        map.set(d.category, [...(map.get(d.category) ?? []), d]);
      }
    }
    return [...map.entries()]
      .sort(([a], [b]) => categoryRank(a) - categoryRank(b))
      .map(([category, items]) => ({
        category,
        items: items.sort((a, b) => a.name.localeCompare(b.name)),
      }));
  });

  const changed = async (keep: boolean) => {
    api.invalidate("/api/v1/devices");
    await refetch();
    if (!keep) {
      navigate("/devices");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow={t("nav.monitoring")}
        title={t("devices.title")}
        sub={t("devices.intro")}
      />
      <Show when={!devices.error} fallback={<ErrorBox error={devices.error} />}>
        <Show
          when={devices()}
          fallback={
            <section class="card">
              <Skeleton />
            </section>
          }
        >
          <Show
            when={(devices()?.length ?? 0) > 0}
            fallback={
              <section class="card empty">
                <h2>{t("devices.emptyTitle")}</h2>
                <p class="muted">{t("devices.empty")}</p>
                <div>
                  <a class="btn primary" href="/integrations">
                    {t("devices.toIntegrations")}
                  </a>
                </div>
              </section>
            }
          >
            <div class="grid g-2 g-2s">
              <section class="card">
                <div class="panel-h">
                  <label class="search">
                    <Icon name="search" />
                    <input
                      type="search"
                      placeholder={t("devices.search")}
                      value={query()}
                      onInput={(e) => setQuery(e.currentTarget.value)}
                    />
                  </label>
                  <span class="hint">
                    {t("devices.count", { count: devices()!.length })}
                  </span>
                </div>
                <div class="scrollx">
                  <table class="tbl">
                    <For each={groups()}>
                      {(g) => (
                        <tbody>
                          <tr class="grp">
                            <td colSpan={2}>
                              <div class="grp-in">
                                <CategoryBadge category={g.category} />
                                <span>{t(`category.${g.category}`)}</span>
                                <span class="cnt">{g.items.length}</span>
                              </div>
                            </td>
                          </tr>
                          <For each={g.items}>
                            {(d) => (
                              <tr
                                class={`click ${d.id === selected()?.id ? "sel" : ""}`}
                                onClick={() => navigate(`/devices/${d.id}`)}
                              >
                                <td>
                                  <div>{d.name}</div>
                                  <div class="hint">{d.templateId}</div>
                                </td>
                                <td class="r">
                                  <Show when={d.sources === 0}>
                                    <span class="chip warn">
                                      {t("devices.noSource")}
                                    </span>
                                  </Show>
                                </td>
                              </tr>
                            )}
                          </For>
                        </tbody>
                      )}
                    </For>
                  </table>
                </div>
              </section>
              <Show when={selected()} keyed>
                {(d) => <DeviceCard deviceId={d.id} onChanged={changed} />}
              </Show>
            </div>
          </Show>
        </Show>
      </Show>
    </>
  );
}

/** Keeps the latest values of one device current from the live hub. */
function useLatest(deviceId: () => string) {
  const [values, { mutate }] = createResource(deviceId, (id) =>
    api.devices.latest(id),
  );
  createEffect(() => {
    const id = deviceId();
    const off = live.on<LatestValue[]>("device.readings", (changed) => {
      const mine = changed.filter((v) => v.deviceId === id);
      if (mine.length === 0) {
        return;
      }
      mutate((list) => {
        const next = new Map(
          (list ?? []).map((v) => [`${v.channel}/${v.attribute}`, v]),
        );
        for (const v of mine) {
          next.set(`${v.channel}/${v.attribute}`, v);
        }
        return [...next.values()];
      });
    });
    const sub = live.subscribe(deviceTopic(id));
    onCleanup(() => {
      off();
      void sub.then((unsubscribe) => unsubscribe());
    });
  });
  return values;
}

function DeviceCard(props: {
  deviceId: string;
  onChanged: (keep: boolean) => Promise<void>;
}) {
  const [detail, { refetch }] = createResource(
    () => props.deviceId,
    (id) => api.devices.get(id),
  );
  const values = useLatest(() => props.deviceId);
  const [tab, setTab] = createSignal<Tab>("values");
  const [chosen, setChosen] = createSignal<{
    channel: string;
    attribute: string;
  }>();
  const [rename, setRename] = createSignal(false);
  const [remove, setRemove] = createSignal(false);
  const [placing, setPlacing] = createSignal(false);
  const navigate = useNavigate();

  const newest = () =>
    (values() ?? [])
      .map((v) => v.at)
      .sort()
      .at(-1) ?? null;
  // Power (or the first numeric value) is charted until the user picks another attribute. Compared by key, so live
  // values do not count as a new choice (that would reload the history on every reading).
  const charted = createMemo(
    () =>
      chosen() ??
      (() => {
        const list = (values() ?? []).filter((v) => v.number !== null);
        const pick =
          list.find((v) => v.attribute === "power.active") ?? list[0];
        return pick
          ? { channel: pick.channel, attribute: pick.attribute }
          : undefined;
      })(),
    undefined,
    {
      equals: (a, b) =>
        a?.channel === b?.channel && a?.attribute === b?.attribute,
    },
  );
  const showHistory = (channel: string, attribute: string) => {
    setChosen({ channel, attribute });
    setTab("history");
  };

  return (
    <section class="card">
      <Show
        when={!detail.error}
        fallback={
          <div class="card-b">
            <ErrorBox error={detail.error} />
          </div>
        }
      >
        <Show when={detail()} fallback={<Skeleton lines={5} />}>
          {(d) => (
            <>
              <div class="card-h">
                <CategoryBadge category={d().category} />
                <h2>{d().name}</h2>
                <span
                  class={`chip ${isStale(newest()) ? "stale" : "ok"}`}
                  title={formatDateTime(newest(), locale())}
                >
                  {newest()
                    ? formatAgo(newest(), locale())
                    : t("devices.noValuesShort")}
                </span>
                <span class="meta">
                  {d().template.vendor} · {d().template.model}
                </span>
              </div>
              <div class="panel-h">
                <span class="eyebrow">{t("place.places")}</span>
                <For
                  each={placesOf(d().id)}
                  fallback={<span class="chip warn">{t("place.nowhere")}</span>}
                >
                  {(p) => (
                    <button
                      type="button"
                      class="chip"
                      title={t(`role.point.${p.point.role}`)}
                      onClick={() => {
                        setScope(p.point.nodeId);
                        navigate("/assets");
                      }}
                    >
                      {p.nodeName} · {p.point.name}
                    </button>
                  )}
                </For>
                <Show when={operator()}>
                  <button
                    type="button"
                    class="btn sm"
                    onClick={() => setPlacing(true)}
                  >
                    <Icon name="link" />
                    {t("place.action")}
                  </button>
                </Show>
              </div>
              <Show when={operator()}>
                <div class="panel-h">
                  <button
                    type="button"
                    class="btn sm"
                    onClick={() => setRename(true)}
                  >
                    <Icon name="edit" />
                    {t("common.rename")}
                  </button>
                  <button
                    type="button"
                    class="btn sm danger"
                    style={{ "margin-left": "auto" }}
                    onClick={() => setRemove(true)}
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </Show>
              <Tabs
                tabs={TABS}
                value={tab()}
                onChange={setTab}
                label={(x) => t(`devices.tab.${x}`)}
              />
              <Show when={tab() === "values"}>
                <Values
                  values={values() ?? []}
                  loading={values.loading}
                  chosen={charted()}
                  onPick={showHistory}
                />
              </Show>
              <Show when={tab() === "history"}>
                <History
                  detail={d()}
                  values={values() ?? []}
                  chosen={charted()}
                  onPick={(channel, attribute) =>
                    setChosen({ channel, attribute })
                  }
                />
              </Show>
              <Show when={tab() === "sources"}>
                <Sources detail={d()} />
              </Show>
              <Show when={tab() === "capabilities"}>
                <Capabilities detail={d()} values={values() ?? []} />
              </Show>

              <Show when={placing()}>
                <PlaceDeviceDialog
                  device={d()}
                  onClose={() => setPlacing(false)}
                />
              </Show>
              <Show when={rename()}>
                <RenameDialog
                  title={t("devices.renameTitle")}
                  value={d().name}
                  onSave={async (name) => {
                    await api.devices.rename(d().id, name);
                    api.invalidate(`/api/v1/devices/${d().id}`);
                    await refetch();
                    await props.onChanged(true);
                  }}
                  onClose={() => setRename(false)}
                />
              </Show>
              <Show when={remove()}>
                <ConfirmDialog
                  title={t("devices.deleteTitle", { name: d().name })}
                  text={t("devices.deleteText")}
                  confirm={t("devices.deleteConfirm")}
                  danger
                  onConfirm={async () => {
                    await api.devices.remove(d().id);
                    await props.onChanged(false);
                  }}
                  onClose={() => setRemove(false)}
                />
              </Show>
            </>
          )}
        </Show>
      </Show>
    </section>
  );
}

function Values(props: {
  values: LatestValue[];
  loading: boolean;
  chosen: { channel: string; attribute: string } | undefined;
  onPick: (channel: string, attribute: string) => void;
}) {
  return (
    <Show
      when={props.values.some(hasValue)}
      fallback={
        <div class="card-b hint">
          {props.loading ? t("common.loading") : t("devices.noValues")}
        </div>
      }
    >
      <div class="card-b">
        <div class="axes">
          <For each={groupByChannel(props.values.filter(hasValue))}>
            {(group) => (
              <div class="axis">
                <span class="eyebrow">
                  {channelLabel(group.channel, group.values[0]?.capability)}
                </span>
                <dl class="vals">
                  <For each={group.values}>
                    {(v) => (
                      <>
                        <dt
                          title={tOr(`attribute.${v.attribute}`, v.attribute)}
                        >
                          {tOr(`attribute.${v.attribute}`, v.attribute)}
                        </dt>
                        <dd
                          class={isStale(v.at) ? "stale" : ""}
                          title={formatDateTime(v.at, locale())}
                        >
                          <Show
                            when={v.number !== null}
                            fallback={formatValue(v, locale(), (k, fb) =>
                              tOr(k, fb),
                            )}
                          >
                            <button
                              type="button"
                              aria-pressed={
                                props.chosen?.channel === v.channel &&
                                props.chosen.attribute === v.attribute
                              }
                              onClick={() =>
                                props.onPick(v.channel, v.attribute)
                              }
                            >
                              {formatValue(v, locale(), (k, fb) => tOr(k, fb))}
                            </button>
                          </Show>
                        </dd>
                      </>
                    )}
                  </For>
                </dl>
              </div>
            )}
          </For>
        </div>
        <p class="hint" style={{ margin: "12px 0 0" }}>
          {t("devices.valuesHint")}
        </p>
      </div>
    </Show>
  );
}

/**
 * The heading of a channel's values. "main" is only the template's name for a device's single or primary channel,
 * so it is named after what it measures (temperature sensor, energy meter); phases and outputs keep their names.
 */
function channelLabel(channel: string, capability: string | undefined): string {
  return channel === "main" && capability
    ? tOr(`capability.${capabilityId(capability)}`, channel)
    : tOr(`channel.${channel}`, channel);
}

function attributeDefinition(
  detail: DeviceDetail,
  channel: string,
  attribute: string,
): AttributeDefinition | undefined {
  const ref = detail.template.channels.find(
    (c) => c.key === channel,
  )?.capability;
  return detail.capabilities
    .find((c) => c.ref === ref)
    ?.attributes.find((a) => a.key === attribute);
}

/** History of one attribute over the context bar's period: instant values as a line, counters as increments. */
function History(props: {
  detail: DeviceDetail;
  values: LatestValue[];
  chosen: { channel: string; attribute: string } | undefined;
  onPick: (channel: string, attribute: string) => void;
}) {
  const numeric = () => props.values.filter((v) => v.number !== null);
  const [series, { mutate }] = createResource(
    () =>
      props.chosen
        ? {
            channel: props.chosen.channel,
            attribute: props.chosen.attribute,
            from: range().from,
            to: range().to,
          }
        : undefined,
    (q) =>
      api.devices.series(
        props.detail.id,
        q.channel,
        q.attribute,
        q.from,
        q.to,
        "15m",
      ),
  );
  // Readings pushed by the live hub extend the line while the period reaches now; history is fetched only once.
  createEffect(() => {
    const c = props.chosen;
    const v = props.values.find(
      (x) => x.channel === c?.channel && x.attribute === c?.attribute,
    );
    const loaded = series.state === "ready" ? series() : undefined;
    if (!v || v.number === null || !loaded || def()?.kind === "interval") {
      return;
    }
    const at = new Date(v.at);
    const last = loaded.at(-1);
    if (at < range().to && (!last || at > new Date(last.at))) {
      mutate([
        ...loaded,
        {
          at: v.at,
          value: v.number,
          min: null,
          max: null,
          text: null,
          quality: v.quality,
        },
      ]);
    }
  });
  const def = () =>
    props.chosen
      ? attributeDefinition(
          props.detail,
          props.chosen.channel,
          props.chosen.attribute,
        )
      : undefined;
  const unit = () => {
    const u =
      def()?.unit ??
      props.values.find(
        (v) =>
          v.channel === props.chosen?.channel &&
          v.attribute === props.chosen?.attribute,
      )?.unit ??
      "";
    return tOr(`unit.${u}`, u);
  };
  const chart = createMemo<ChartSeries[]>(() => {
    const points = (series() ?? []).map((p) => ({
      t: new Date(p.at).getTime(),
      v: p.value,
    }));
    const label = props.chosen
      ? tOr(`attribute.${props.chosen.attribute}`, props.chosen.attribute)
      : "";
    if (def()?.kind === "interval") {
      return [{ key: "sum", label, color: "var(--m-el)", kind: "bar", points }];
    }
    if (def()?.kind === "counter") {
      return [
        {
          key: "inc",
          label: t("devices.increment", { name: label }),
          color: "var(--m-el)",
          kind: "bar",
          points: counterIncrements(points),
        },
      ];
    }
    return [
      { key: "avg", label, color: "var(--accent)", kind: "area", points },
      ...((series() ?? []).some((p) => p.max !== null && p.max !== p.value)
        ? [
            {
              key: "max",
              label: t("devices.max"),
              color: "var(--faint)",
              kind: "line" as const,
              dashed: true,
              points: (series() ?? []).map((p) => ({
                t: new Date(p.at).getTime(),
                v: p.max,
              })),
            },
          ]
        : []),
    ];
  });

  return (
    <div class="card-b stack">
      <div class="field">
        <label for="histAttr">{t("devices.attribute")}</label>
        <select
          id="histAttr"
          class="inp"
          value={
            props.chosen
              ? `${props.chosen.channel}|${props.chosen.attribute}`
              : ""
          }
          onChange={(e) => {
            const [channel, attribute] = e.currentTarget.value.split("|");
            props.onPick(channel!, attribute!);
          }}
        >
          <For each={numeric()}>
            {(v) => (
              <option value={`${v.channel}|${v.attribute}`}>
                {tOr(`channel.${v.channel}`, v.channel)} ·{" "}
                {tOr(`attribute.${v.attribute}`, v.attribute)}
              </option>
            )}
          </For>
        </select>
      </div>
      <Show when={!series.error} fallback={<ErrorBox error={series.error} />}>
        <Show when={!series.loading} fallback={<Skeleton lines={6} />}>
          <Chart
            series={chart()}
            from={range().from}
            to={range().to}
            unit={unit()}
            locale={locale()}
            bucketMs={15 * 60_000}
            emptyText={t("devices.noHistory")}
          />
        </Show>
      </Show>
    </div>
  );
}

function Sources(props: { detail: DeviceDetail }) {
  return (
    <div class="card-b stack">
      <Show
        when={props.detail.sources.length > 0}
        fallback={<p class="hint">{t("devices.noSourceText")}</p>}
      >
        <table class="tbl">
          <thead>
            <tr>
              <th>{t("devices.connection")}</th>
              <th>{t("devices.sourceId")}</th>
            </tr>
          </thead>
          <tbody>
            <For each={props.detail.sources}>
              {(s) => (
                <tr>
                  <td>
                    <a href={`/integrations/${s.connectionId}`}>
                      {s.connectionName}
                    </a>
                    <div class="hint">
                      {tOr(`adapter.${s.adapter}`, s.adapter)}
                    </div>
                  </td>
                  <td class="mono">{s.sourceDeviceId}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </Show>
      <dl class="kv">
        <dt>{t("devices.id")}</dt>
        <dd class="mono">{props.detail.id}</dd>
        <dt>{t("devices.template")}</dt>
        <dd class="mono">{props.detail.template.id}</dd>
        <dt>{t("devices.created")}</dt>
        <dd>{formatDateTime(props.detail.createdAt, locale())}</dd>
      </dl>
    </div>
  );
}

/**
 * What the device measures: template attributes it has reported a value for. A template covers every variant of
 * a model (three phases, all registers); a single-phase meter reports only part of it, and SUPLA sends values a
 * meter does not measure as gaps. Until the device reports anything, the template is all there is to show.
 */
function Capabilities(props: { detail: DeviceDetail; values: LatestValue[] }) {
  const reported = () =>
    new Set(
      props.values.filter(hasValue).map((v) => `${v.channel}/${v.attribute}`),
    );
  const known = () => reported().size > 0;
  const channels = () =>
    props.detail.template.channels
      .map((ch) => {
        const cap = props.detail.capabilities.find(
          (c) => c.ref === ch.capability,
        );
        const attributes = (cap?.attributes ?? []).filter(
          (a) =>
            ch.mappings.some((m) => m.attribute === a.key) &&
            (!known() || reported().has(`${ch.key}/${a.key}`)),
        );
        return { ch, attributes };
      })
      .filter((c) => c.attributes.length > 0);
  return (
    <div class="card-b stack">
      <Show when={!known()}>
        <p class="hint" style={{ margin: 0 }}>
          {t("devices.capabilitiesTemplate")}
        </p>
      </Show>
      <For each={channels()}>
        {({ ch, attributes }) => (
          <div class="axis">
            <span class="eyebrow">
              {tOr(`channel.${ch.key}`, ch.key)} ·{" "}
              {tOr(`capability.${capabilityId(ch.capability)}`, ch.capability)}
            </span>
            <div class="q">
              <For each={attributes}>
                {(a) => (
                  <span class="chip">
                    {tOr(`attribute.${a.key}`, a.key)} · {t(`kind.${a.kind}`)}
                  </span>
                )}
              </For>
            </div>
          </div>
        )}
      </For>
    </div>
  );
}
